import { formatCount, tt } from '@agos/shared'

/**
 * A stock item, its level, and the units it is measured in.
 *
 * The restored data holds **135 items**, and two things about it shape this screen:
 *
 *  - **99 of them are below their minimum.** A badge on three rows in four does not
 *    inform anybody, so the list is ordered worst-first rather than left in API
 *    order — the same reasoning as FB-057 on the vehicle fleet.
 *  - **Five have a unit the form does not offer** — `Liter` once, and `fľaša` on the
 *    four Amicius wines, none of which has a cost unit of its own. Legacy guards the
 *    item's own unit select against that and *not* the cost unit's, which is FB-077.
 */

export type StockType = 'raw_material' | 'product'

export interface StockItem {
  id?: number
  name?: string
  type?: string
  category?: string
  unit?: string
  qty?: number
  minQty?: number
  active?: boolean
  productId?: number | null
  /** A price per unit — a rate, not an amount somebody pays (FB-056). */
  costPrice?: number | null
  costUnit?: string | null
  defaultSupplierId?: number | null
}

export type StockLevel = 'critical' | 'low' | 'ok'

/**
 * How badly an item is running out.
 *
 * Legacy's bands, kept: below the minimum is critical, within half again of it is
 * low. **A minimum of zero means "not tracked", not "everything is critical"** —
 * reading it as a threshold would divide by zero and pin every such item to the top
 * of a worst-first list forever.
 */
export function stockLevel(item: StockItem): StockLevel {
  const minQty = item.minQty ?? 0
  if (minQty <= 0) return 'ok'
  const qty = item.qty ?? 0
  const ratio = qty / minQty
  if (ratio < 1) return 'critical'
  if (ratio < 1.5) return 'low'
  return 'ok'
}

export type BadgeColor = 'red' | 'amber' | 'green' | 'blue' | 'zinc'

const LEVEL_COLORS: Record<StockLevel, BadgeColor> = {
  critical: 'red',
  low: 'amber',
  ok: 'green',
}

export function levelColor(level: StockLevel): BadgeColor {
  return LEVEL_COLORS[level]
}

export function levelLabel(level: StockLevel): string {
  if (level === 'critical') return tt('Kritický')
  if (level === 'low') return tt('Nízky')
  return tt('V poriadku')
}

const LEVEL_RANK: Record<StockLevel, number> = { critical: 0, low: 1, ok: 2 }

/**
 * Worst first, then by name.
 *
 * Within a level, by how far under the minimum the item is — 0,5 kg against a 5 kg
 * minimum is more urgent than 4,5 kg against the same, and both are "critical".
 */
export function byUrgency(items: StockItem[]): StockItem[] {
  return [...items].sort((left, right) => {
    const rank = LEVEL_RANK[stockLevel(left)] - LEVEL_RANK[stockLevel(right)]
    if (rank !== 0) return rank
    // Compared for inequality first: two untracked items are both `Infinity`, and
    // `Infinity - Infinity` is NaN, which `sort` reads as "leave them alone" — so the
    // name tie-break below would never be reached.
    const leftCoverage = coverage(left)
    const rightCoverage = coverage(right)
    if (leftCoverage !== rightCoverage) return leftCoverage - rightCoverage
    return (left.name ?? '').localeCompare(right.name ?? '', 'sk')
  })
}

/** Stock as a fraction of the minimum. `Infinity` when no minimum is set. */
export function coverage(item: StockItem): number {
  const minQty = item.minQty ?? 0
  if (minQty <= 0) return Number.POSITIVE_INFINITY
  return (item.qty ?? 0) / minQty
}

export interface StockFilter {
  type: string
  category: string
  search: string
  /** §9.136 — *deactivated* is not *deleted*, so there is a way back to them. Default off. */
  includeInactive?: boolean
}

export function filterItems(
  items: StockItem[],
  { type, category, search, includeInactive = false }: StockFilter,
): StockItem[] {
  const needle = search.trim().toLowerCase()
  return items.filter((item) => {
    // §9.136 — a switched-off row is not a stock level, it is a row somebody has finished with.
    // Measured: 62 of 192 items here are inactive, every one at zero, a third of the list. Same rule
    // and same reason as §9.131 gave for products on the till.
    //
    // `!== false` rather than `=== true`: a row that omits the field is real, and reading a missing
    // column as *off* would empty the store room.
    if (!includeInactive && item.active === false) return false
    if (type !== 'all' && item.type !== type) return false
    if (category !== 'all' && (item.category ?? '') !== category) return false
    if (needle && !(item.name ?? '').toLowerCase().includes(needle)) return false
    return true
  })
}

/** The categories actually in use, sorted — legacy builds the filter from the items. */
export function usedCategories(items: StockItem[]): string[] {
  return [...new Set(items.map((item) => item.category).filter((category): category is string => Boolean(category)))].sort(
    (left, right) => left.localeCompare(right, 'sk'),
  )
}

export interface LevelCounts {
  total: number
  critical: number
  low: number
  /** How many have no minimum, so nothing is being watched for them. */
  untracked: number
}

export function levelCounts(items: StockItem[]): LevelCounts {
  return {
    total: items.length,
    critical: items.filter((item) => stockLevel(item) === 'critical').length,
    low: items.filter((item) => stockLevel(item) === 'low').length,
    untracked: items.filter((item) => (item.minQty ?? 0) <= 0).length,
  }
}

// ─── units ───────────────────────────────────────────────────────────────────

/** Synced with the product editor's list, plus the units supplier invoices use. */
export const UNIT_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'ks', label: 'ks (kus)' },
  { value: 'kg', label: 'kg (kilogram)' },
  { value: 'g', label: 'g (gram)' },
  { value: 'l', label: 'l (liter)' },
  { value: 'ml', label: 'ml (mililiter)' },
  { value: 'cl', label: 'cl (centiliter)' },
  { value: 'porcia', label: tt('porcia') },
  { value: 'zv', label: tt('zv (zväzok)') },
  { value: 'ba', label: tt('ba (balík)') },
  { value: 'kt', label: tt('kt (karton)') },
] as const

export function isKnownUnit(unit: string | null | undefined): boolean {
  if (!unit) return false
  return UNIT_OPTIONS.some((option) => option.value === unit)
}

/**
 * The options a unit select must offer so it cannot rewrite what it was given.
 *
 * **FB-077.** Legacy does this for the item's own unit and forgets it for the cost
 * unit, whose select offers the ten known units only. Four items in the data are
 * priced per `fľaša`; opening one and saving anything writes `costUnit: "ks"`,
 * because that is the option the browser had selected all along. A price per bottle
 * silently becomes a price per piece, and it feeds recipe costing and the margin
 * table.
 */
export function unitOptionsFor(current: string | null | undefined): { value: string; label: string }[] {
  const options = [...UNIT_OPTIONS]
  if (current && !isKnownUnit(current)) {
    options.push({ value: current, label: `${current} ${tt('(vlastná)')}` })
  }
  return options
}

/** The unit a cost is quoted in — the item's own, unless one was set. */
export function costUnitOf(item: StockItem): string {
  const costUnit = item.costUnit?.trim()
  return costUnit && costUnit !== '' ? costUnit : (item.unit ?? '')
}

// ─── movements ───────────────────────────────────────────────────────────────

export type MovementType = 'in' | 'out' | 'adjustment' | 'waste'

export interface StockMovement {
  id?: number
  stockItemId?: number
  stockItemName?: string | null
  stockItemUnit?: string | null
  type?: string
  qty?: number
  note?: string | null
  reference?: string | null
  createdBy?: string | null
  createdAt?: string
}

const MOVEMENT_LABELS: Record<MovementType, string> = {
  in: tt('Príjem'),
  out: tt('Výdaj'),
  adjustment: tt('Korekcia'),
  waste: tt('Odpis'),
}

export function movementLabel(type: string | undefined): string {
  if (!type) return ''
  return MOVEMENT_LABELS[type as MovementType] ?? type
}

const MOVEMENT_COLORS: Record<MovementType, BadgeColor> = {
  in: 'green',
  out: 'red',
  adjustment: 'blue',
  waste: 'red',
}

export function movementColor(type: string | undefined): BadgeColor {
  return MOVEMENT_COLORS[type as MovementType] ?? 'zinc'
}

/** In adds, out and waste take away, a correction is whatever it says. */
export function signedQty(movement: StockMovement): number {
  const qty = movement.qty ?? 0
  if (movement.type === 'out' || movement.type === 'waste') return -Math.abs(qty)
  if (movement.type === 'in') return Math.abs(qty)
  return qty
}

/**
 * *Zobrazených 500 z 111 881* - said only when the server counted more than it sent.
 *
 * FB-937, `FRONTEND.md §9.628`. This replaces `filterMovements`, which narrowed the list in the
 * browser over whatever the server's cap had already thrown away.
 *
 * **Off the server's own total, never off the list.** A count taken from the rows it describes cannot
 * tell a short page from a truncated one - §3.48's rule, and the whole reason §10A.317 answers a
 * `total` at all. Silent when `total <= shown`, because a list that is whole has nothing to report,
 * and the day this screen gains paging the same expression still reads correctly.
 *
 * **Not `truncationNote`**, which `manager-inventory` already has on the supplier comparison: that one
 * answers *your filter hid these*, and this one answers *the server did not send them*.
 */
export function movementCapNote(shown: number, total: number): string {
  if (total <= shown) return ''
  // Grouped, because 111 881 is a figure somebody reads rather than a token - `formatCount`'s own reason.
  return `${tt('Zobrazených')} ${formatCount(shown)} ${tt('z')} ${formatCount(total)}`
}
